import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IUserMessageIndexModel } from 'src/app/messages/interfaces/user-message-index-model.interface';
import { MessageTypeEnum } from '../../interfaces/system-message.interface';
import { AuthState } from '../../states/auth/auth.state';
import { ProfileState } from '../../states/profile/profile.state';
import { DeleteUserMessage, GetUnreadMessagesSummary, MarkMessageRead } from '../../states/user-message/user-message.actions';
import { UserMessageState } from '../../states/user-message/user-message.state';
import { RouteTrackingService } from '../../services/route-tracking.service';

@UntilDestroy()
@Component({
  selector: 'app-user-message-dialog',
  templateUrl: './user-message-dialog.component.html',
  styleUrls: ['./user-message-dialog.component.scss'],
  animations: [
      trigger('fadeInOut', [
        transition(':enter', [
          style({opacity: 0}),
          animate('200ms', style({opacity: 1})),
        ]),
        transition(':leave', [
          animate('200ms', style({opacity: 0})),
        ]),
      ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMessageDialogComponent implements OnInit {

  @Select(UserMessageState.latestUnread) latestUnread$: Observable<IUserMessageIndexModel>;

  MessageTypeEnum = MessageTypeEnum;
  extended: boolean;
  
  isMessageNotInAuthScreen$ = new BehaviorSubject(false);                                                                                     
  /**
   *
   */
  constructor(private store: Store, private routeTrackingService: RouteTrackingService) {
    
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    const message = this.store.selectSnapshot(UserMessageState.latestUnread);
    if (message && message.type === MessageTypeEnum.DeploymentSuccess) {
      this.store.dispatch(new DeleteUserMessage(message.userMessageId, message.messageId))
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(AuthState.activeUser), 
      this.store.select(AuthState.lockScreen), 
      this.store.select(ProfileState.dashboardData)
    ])
        .pipe(untilDestroyed(this), distinctUntilChanged(), debounceTime(1000))
        .subscribe(([user, screenLocked, dashboardData]) => {
         if (user && !screenLocked && dashboardData)
          this.store.dispatch(new GetUnreadMessagesSummary(user.id));
      });


    this.routeTrackingService.currentRoute$
    .pipe(untilDestroyed(this))
    .subscribe(route => {
      if (route) {
        this.isMessageNotInAuthScreen$.next(!route.includes('auth'));
      }
    });
  }

  readMore(): void {
    this.extended = true;
  }

  gotIt(id: string): void {
    this.extended = false;
    const activeUserId = this.store.selectSnapshot(AuthState.activeUser)?.id;
    this.store.dispatch(new MarkMessageRead(activeUserId, id));
  }

  refresh(): void {
    const message = this.store.selectSnapshot(UserMessageState.latestUnread);
    this.store.dispatch(new DeleteUserMessage(message.userMessageId, message.messageId))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      window.location.reload();
    })
  }
}
